import type { ReactElement } from 'react';
import React from 'react';
import {
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  FormTitle,
  Input,
  ModalFooter,
  Select,
  Text,
  View,
} from '../ui';
import { Controller, useForm } from 'react-hook-form';
import * as zod from 'zod';
import LocationSelect from '../LocationSelect';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import moment from 'moment';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { Event, SingleEvent, Events } from '../core/TimeData';
import { SingleEventType } from '../core/TimeData';
import { hToMs, msToH } from '../../lib/time';
import { dateStringToNum } from '../core/util';
import { singleEventTypes } from '../shipment/ShippingPlanTypes';
import type { EventTimeline } from '../core/Timelines';
import { useCurrentUser } from '../../state/zustand';
import LoadingSpinner from '../LoadingSpinner';
import { roundTo } from '../../lib/util';

const locationSchema = zod.object({
  value: zod.number(),
  label: zod.string(),
  location: zod.object({ timezone: zod.string().nullable() }).nullable(),
});

const _singleEventSchema = zod
  .object({
    start: zod.string(),
    duration: zod.number(),
    location: locationSchema,
    type: zod.nativeEnum(SingleEventType),
  })
  .refine((values) => values.duration != null && values.duration > 0, {
    message: 'duration is not greater than zero',
    path: ['duration'],
  });

type SingleEventFormFields = zod.infer<typeof _singleEventSchema>;

export interface SingleEventFormProps {
  isLoading?: boolean;
  vesselId: number;
  initialStartTime?: number;
  scheduleContext: ScheduleDataContext;
  handleCreateEvent?: (
    type: SingleEventType,
    vesselId: number,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    duration: number,
    index: number,
    start: number,
    modalKey?: string,
  ) => void;
  handleUpdateEntry?: (
    timeline: EventTimeline,
    event: Event,
    change: Partial<Events>,
  ) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  singleEvent: SingleEvent | null;
  timeline: EventTimeline;
}

const SingleEventForm = ({
  singleEvent,
  handleCreateEvent,
  handleUpdateEntry,
  onCancel,
  onDelete,
  vesselId,
  isLoading,
  initialStartTime,
  scheduleContext,
  timeline,
}: SingleEventFormProps): ReactElement => {
  const currentUser = useCurrentUser();

  const isEdit = singleEvent != null;

  const location =
    singleEvent?.locationId !== undefined
      ? scheduleContext.locations.get(singleEvent.locationId)
      : null;

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<SingleEventFormFields>({
    defaultValues: {
      start: moment(singleEvent?.start ?? initialStartTime)
        .startOf('hour')
        .format(),
      duration: singleEvent?.duration
        ? roundTo(msToH(singleEvent.duration), 2)
        : 1,
      location: singleEvent
        ? {
            label: location?.name ?? undefined,
            value: location?.id,
            location,
          }
        : undefined,
      type: singleEvent?.event ?? SingleEventType.OTHER,
    },
  });

  const selectedLocation = watch('location');

  const errorKeys = Object.keys(errors);

  const onSubmit = async (data: SingleEventFormFields) => {
    if (
      currentUser === undefined ||
      currentUser?.id === undefined ||
      currentUser?.organizationId === undefined
    ) {
      throw new Error(`Cannot save singleEvent without current user in store`);
    }
    const start = dateStringToNum(data.start);
    const location = data.location;
    const locationId = location.value;
    if (!locationId) {
      throw new Error('locationId is undefined');
    }
    const tcTimeline = timeline.voyageGroups[0];
    const after = tcTimeline.events.find((e) => e.start > start);

    if (singleEvent) {
      if (!handleUpdateEntry) {
        throw new Error('handleUpdateEntry handler missing');
      }
      handleUpdateEntry(timeline, singleEvent, {
        locationId,
        start,
        startOverride: start,
        duration: hToMs(data.duration),
        event: +data.type,
      });
    } else {
      if (!handleCreateEvent) {
        throw new Error('handleCreateEvent handler missing');
      }
      handleCreateEvent(
        +data.type,
        vesselId,
        locationId,
        location.label,
        location.location?.timezone ?? null,
        hToMs(data.duration),
        after?.index || -1,
        start,
      );
    }
  };

  const typeOptions = singleEventTypes.map((t) => ({
    id: t.type,
    label: capitalize(t.label),
  }));

  return (
    <View height="100%">
      <Form
        style={{ overflowY: 'visible', borderBottom: 'none' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormSection
          borderBottomStyle="none"
          marginTop={2}
          marginLeft={-2}
          marginBottom={-3}
        >
          <FormTitle>
            <Text fontSize="1.4em" fontWeight="bold">
              {isEdit ? 'Edit single event' : 'New single event'}
            </Text>
          </FormTitle>
        </FormSection>
        <FormSection zIndex={4} borderBottomStyle="none">
          <FormRow height={60}>
            <FormField
              label="Port"
              width="100%"
              required
              hasError={!!errors.location}
            >
              <Controller
                control={control}
                name="location"
                render={({ field }) => <LocationSelect {...field} />}
                rules={{ required: true }}
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              label="Type"
              width="100%"
              required
              hasError={!!errors.type}
            >
              <Select
                {...register('type')}
                options={typeOptions}
                enablePlaceholder
                required
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              label={
                'Start time ' + (selectedLocation?.location?.timezone ?? 'UTC')
              }
              width="100%"
              hasError={!!errors.start}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="start"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    onChange={onChange}
                    selected={value ? moment(value).toDate() : null}
                    showTime
                    showTimeInput
                    timeZone={selectedLocation?.location?.timezone ?? undefined}
                  />
                )}
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              label="Duration (h)"
              width="100%"
              hasError={!!errors.duration}
            >
              <Input
                {...register('duration')}
                type="number"
                required
                min={0}
                step={1}
              />
            </FormField>
          </FormRow>
        </FormSection>

        {errorKeys.length > 0 && (
          <FormSection>
            <Text color="#9d0300">
              {errorKeys
                .map((key) => `${startCase(key)} is not valid.`)
                .join(' ')}
            </Text>
          </FormSection>
        )}
      </Form>

      <div style={{ flexGrow: 1 }} />

      <ModalFooter>
        {onCancel && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={onCancel}
            marginRight="3"
          >
            Discard changes & Close
          </Button>
        )}
        {isEdit && onDelete && (
          <Button
            variant="negative"
            disabled={isLoading}
            onPress={onDelete}
            marginRight="3"
          >
            Delete
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        {isLoading && (
          <View flexDirection="row" mr={3}>
            <Text color="white" mr={3}>
              Saving changes
            </Text>
            <LoadingSpinner variant="light" />
          </View>
        )}
        <Button disabled={isLoading} onPress={handleSubmit(onSubmit)}>
          {'Save & Close'}
        </Button>
      </ModalFooter>
    </View>
  );
};

export default SingleEventForm;
